import {I18n} from 'aws-amplify/utils';
import {
    Text,
    Tooltip,
} from '@mantine/core';

import {GroupedRecordCount} from '../zoneGrouping';

// FNP-1378 (REOPENED) — THE RECORD COUNT WHEN SEVERAL CONNECTIONS ANSWERED.
//
// ⚠️ A THIRD STATE, AND IT IS NOT A STYLED VERSION OF EITHER OTHER ONE. This is
// `RecordCount`'s two-state problem with a case added, and the added case is
// the one grouping created: two connections reading ONE zone can report
// DIFFERENT counts — a narrower role may not see every record set, and the two
// reads happen at different moments anyway.
//
// A grouped row that silently picked one of the numbers would assert something
// no connection said. That is exactly the failure `RecordCount` already guards
// against at the level below — "not reported" rendered as "0" tells an operator
// a zone nobody counted is empty — and the fix here is the same shape: make the
// uncertainty visible rather than resolve it by picking.
//
// ⚠️ WHY THIS MATTERS MORE THAN IT LOOKS. An operator reading a record count is
// usually deciding whether a zone is safe to delete. A number that is really
// "one of two disagreeing numbers" is worse than no number, because it carries
// the same confidence as a number everyone agreed on.
//
// Its own file because a module that also exports a hook or a constant breaks
// Fast Refresh, and this lints at --max-warnings 0.
export const GroupedRecordCountCell = ({count}: { count: GroupedRecordCount }) => {

    if (count.kind === 'NOT_REPORTED') {
        return (
            <Tooltip
                label={I18n.get('This provider did not report a record count for the zone. That is not the same as the zone being empty.')}
                withArrow={true}
            >
                <Text size={'sm'} c={'dimmed'}>—</Text>
            </Tooltip>
        );
    }

    if (count.kind === 'DISAGREED') {
        // ⚠️ THE NUMBERS ARE SHOWN, not hidden behind a warning word. An
        // operator who can see "4 / 6" knows the scale of the disagreement and
        // which connection to go and check; a bare "conflict" sends them
        // hunting with nothing to go on.
        return (
            <Tooltip
                label={I18n.get('The connections that reach this zone reported different record counts. Open the zone to read one connection at a time.')}
                withArrow={true}
                multiline={true}
                w={280}
            >
                <Text size={'sm'} c={'orange'}>
                    {count.counts.join(' / ')}
                </Text>
            </Tooltip>
        );
    }

    // A REAL count, including a real zero, that every connection agreed on.
    return <Text size={'sm'}>{count.count}</Text>;
};
